import NextLink from 'next/link';
import {useRouter} from 'next/router';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalProps,
  AccordionIcon,
  Text,
  Button,
  ModalFooter,
  Flex,
  FlexProps,
} from '@chakra-ui/react';
import {motion} from 'framer-motion';

import {useTranslate} from '@eksab/i18n';

import {useUserProfileQuery} from '@eksab/features/profile/users/hooks';

import {ParticipantGameDataModalHeader} from './ParticipantGameDataModalHeader';
import {useCompetitionQuery, useParticipantPredictionsQuery} from '../hooks';
import {GameScoreInput, PointsEarnedBadge, QuestionAndAnswer} from '../matches/predictions-modal';
import type {Bet, Participant} from '../types';

interface ParticipantPredictionsModal extends Omit<ModalProps, 'children'> {
  participant: Participant | null;
}

export function ParticipantPredictionsModal({participant, ...props}: ParticipantPredictionsModal) {
  const competitionID = useRouter().query.slug as string;

  const t = useTranslate();
  const {data: competition} = useCompetitionQuery(competitionID);
  const participantPredictionsQuery = useParticipantPredictionsQuery(
    {participantID: participant?.user.id, seasonId: competition?.season.id},
    {enabled: props.isOpen && competition?.state !== 'upcoming'},
  );
  const userProfileQuery = useUserProfileQuery(participant?.user.id, {enabled: props.isOpen});

  if (!participant) return null;

  return (
    <Modal motionPreset="slideInBottom" size="2xl" variant="full" {...props}>
      <ModalOverlay />

      <ModalContent gap="4" px={[2, 5]} pb={[4, 6]} rounded="md" minH="50vh" overflowY="auto">
        <ParticipantGameDataModalHeader
          participant={participant}
          participantProfile={userProfileQuery.data}
          mt={[4, 6]}
        />

        <ModalFooter p="0" mb="6">
          <Button as={NextLink} href={`/profile/${participant.user.id}`} flex="1" fontWeight="700" minH="14">
            {t('profile.users.check-profile')}
          </Button>
        </ModalFooter>

        <ModalBody display="flex" flexDir="column" gap="4" p="0">
          <PointsEarnedBadge points={+participant.points} alignSelf="center" />

          {competition?.state === 'upcoming' ? (
            <Text m="auto" textAlign="center" fontWeight="500" color="gray.500" maxW="70%">
              {t('competition.ranking.tactic.await-competition-start')}
            </Text>
          ) : participantPredictionsQuery.isLoading ? (
            <Flex flexDir="column" gap="2">
              {Array.from({length: 4}).map((_, i) => (
                <GameScoreShimmer key={i} />
              ))}
            </Flex>
          ) : participantPredictionsQuery.isError ? (
            <Text m="auto" textAlign="center">
              {t('app.errors.try-again')}
            </Text>
          ) : (
            <Accordion allowToggle>
              {participantPredictionsQuery.data.map((game) => (
                <AccordionItem key={game.id} isolation="isolate" border="0" isDisabled={!game.bets.length}>
                  <AccordionButton
                    pos="sticky"
                    top="0"
                    bg="white"
                    py="0"
                    _hover={{bg: 'gray.600'}}
                    _expanded={{bg: 'gray.600'}}
                    _disabled={{_hover: {bg: 'white', cursor: 'initial'}}}
                  >
                    <GameScoreInput
                      key={game.id}
                      match={{
                        ...game.match,
                        home_goals: game?.game_score_bet?.participant_prediction?.value.home_goals ?? '',
                        away_goals: game?.game_score_bet?.participant_prediction?.value.away_goals ?? '',
                      }}
                      editable={false}
                      bgColor="unset"
                    />

                    {!!game.bets.length && <AccordionIcon />}
                  </AccordionButton>

                  <AccordionPanel>
                    {game.bets.map((bet) => (
                      <QuestionAndAnswer
                        key={bet.id}
                        matchState={game.state}
                        bet={{...bet, current_user_prediction: bet.participant_prediction} as Bet}
                        answer={bet.participant_prediction?.value}
                        // TODO: Answer cannot change, this is display only
                        // Change the component's API to work without passing `onAnswerChange`
                        onAnswerChange={() => {}}
                      />
                    ))}
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

const GameScoreShimmer = (props: FlexProps) => (
  <Flex
    as={motion.div}
    shrink="0"
    w="full"
    minH="6rem"
    rounded="sm"
    animate={{
      backgroundColor: ['#f3f3f3', '#ecebeb'],
      transition: {duration: 1, repeat: Infinity, repeatType: 'reverse'},
    }}
    {...props}
  />
);
